import up from 'unpoly';

import { $$, $ } from '../../../../utils/dom.ts';
import toggleBodyLock from '../../../../utils/lockBodyScroll.js';
import { pushToDataLayer } from '../../../tracking.ts';

const dcn = (suffix = '') => `.view-components-blocks-CourseFilter${suffix}`;

// Mirrors the `order` radio rendered by App\View\Components\Blocks\CourseFilter.
// It rides along in the same forms as the filter checkboxes but sorts rather
// than filters, so it has to be kept out of the active filter set.
const ORDER_NAME = 'order';
const DEFAULT_ORDER = 'start_date';

class Filter {
    #courseIdToNameToValues;

    #activeFilters = [];

    #activeNames = [];

    constructor(courseIdToNameToValues) {
        this.#courseIdToNameToValues = courseIdToNameToValues;
    }

    // Mutators

    #updateActiveNames() {
        let activeNames = this.#activeFilters.map(([n]) => n);
        activeNames = new Set(activeNames);
        activeNames = Array.from(activeNames.values());

        this.#activeNames = activeNames;
    }

    activateFilter(name, value) {
        this.deactivateFilter(name, value);
        this.#activeFilters.push([name, value]);

        this.#updateActiveNames();
    }

    deactivateFilter(name, value) {
        this.#activeFilters = this.#activeFilters.filter(
            ([n, v]) => !(name === n && value === v),
        );

        this.#updateActiveNames();
    }

    clear() {
        this.#activeFilters = [];
        this.#activeNames = [];
    }

    updateFiltersFromForm($form) {
        const formData = new FormData($form);

        const activeFilters = [];

        Array.from(formData.entries()).forEach(([key, value]) => {
            if (key === ORDER_NAME) {
                return;
            }

            if (key.endsWith('[]')) {
                key = key.slice(0, -2);
            }

            activeFilters.push([key, value]);
        });

        this.#activeFilters = activeFilters;

        this.#updateActiveNames();
    }

    // Accessors

    get hasActive() {
        return this.#activeFilters.length !== 0;
    }

    get activeCount() {
        return this.#activeFilters.length;
    }

    get activeNames() {
        return this.#activeNames;
    }

    isActive(name, value) {
        return !!this.#activeFilters.find(
            ([n, v]) => name === n && value.toString() === v,
        );
    }

    getFilterCount(name) {
        let count = 0;

        this.#activeFilters.forEach(([n]) => {
            if (n === name) {
                count += 1;
            }
        });

        return count;
    }

    courseMatches(courseId) {
        const nameToValues = this.#courseIdToNameToValues[courseId];

        return this.#activeNames.every((name) => {
            const values = nameToValues[name];

            if (values.length === 1 && values[0] === '*') {
                return true;
            }

            return values.some(
                (value) =>
                    !!this.#activeFilters.find(
                        ([n, v]) => name === n && value.toString() === v,
                    ),
            );
        });
    }

    inputIsEnabled(name, value) {
        if (this.isActive(name, value)) {
            return true;
        }

        const prospectiveFilter = this.clone();

        prospectiveFilter.#activeFilters =
            prospectiveFilter.#activeFilters.filter(([n]) => name !== n);
        prospectiveFilter.#updateActiveNames();

        prospectiveFilter.activateFilter(name, value);

        return Object.keys(this.#courseIdToNameToValues).some((courseId) =>
            prospectiveFilter.courseMatches(courseId),
        );
    }

    clone() {
        const clone = new Filter(this.#courseIdToNameToValues);
        clone.#activeNames = this.#activeNames;
        clone.#activeFilters = this.#activeFilters;

        return clone;
    }
}

up.compiler(dcn(), ($el) => {
    // Elements

    const $stickyContainer = $(dcn('-stickyContainer'), $el);
    const $sideFormContainer = $(dcn('-sideFormContainer'), $el);
    const $sideForm = $(dcn('-sideForm'), $el);
    const $sideClearButton = $(dcn('-sideClearButton'), $el);
    const $inputs = $$(dcn('-input'), $el);
    const $orderInputs = $$(dcn('-orderInput'), $el);
    const $inputButtons = $$(dcn('-inputButton'), $el);
    const $inputCounts = $$(dcn('-inputCount'), $el);
    const $pillsContainer = $(dcn('-pillsContainer'), $el);
    const $pills = $$(dcn('-pill'), $el);
    const $pillButtons = $$(dcn('-pillButton'), $el);
    const $stickyCoursesContainer = $(dcn('-stickyCoursesContainer'), $el);
    const $stickyCoursesList = $(dcn('-stickyCoursesList'), $el);
    const $coursesContainer = $(dcn('-coursesContainer'), $el);
    const $courses = $$(dcn('-course'), $coursesContainer);
    const $noMatchesMessage = $(dcn('-noMatchesMessage'), $el);
    const $openOffCanvasFormButton = $(dcn('-openOffCanvasFormButton'), $el);
    const $offCanvasFormContainer = $(dcn('-offCanvasFormContainer'), $el);
    const $offCanvasForm = $(dcn('-offCanvasForm'), $el);
    const $offCanvasCloseButton = $(dcn('-offCanvasCloseButton'), $el);
    const $offCanvasClearButton = $(dcn('-offCanvasClearButton'), $el);

    // Data

    const courseIdToNameToValues = {};

    $courses.forEach(($course) => {
        const { id } = $course.dataset;
        const nameToValues = JSON.parse($course.dataset.nameToValues);

        courseIdToNameToValues[id] = nameToValues;
    });

    // Both lists, because the sticky list holds a second copy of some of the
    // same courses and gets re-sorted alongside the main one.
    const courseIdToOrderToIndex = {};

    $$(dcn('-course'), $el).forEach(($course) => {
        const { id } = $course.dataset;

        courseIdToOrderToIndex[id] = JSON.parse($course.dataset.orderToIndex);
    });

    // State

    let $currentPopover = null;

    const filter = new Filter(courseIdToNameToValues);

    let offCanvasFormIsOpen = false;
    let offCanvasFormChangedWhileOpen = false;

    let order =
        $orderInputs.find(($input) => $input.checked)?.value ?? DEFAULT_ORDER;

    // The server renders the list already sorted by the initial order, so
    // nothing needs moving until the visitor picks a different one.
    let appliedOrder = order;

    let oldElTop = $el.getBoundingClientRect().top;

    // DOM Mutators

    const updateInputCounts = () => {
        $inputCounts.forEach(($inputCount) => {
            $inputCount.innerText =
                filter.getFilterCount($inputCount.dataset.name) || '';
        });
    };

    const updateInputs = () => {
        $inputs.forEach(($input) => {
            let { name } = $input;
            const { value } = $input;

            if (name.endsWith('[]')) {
                name = name.slice(0, -2);
            }

            $input.checked = filter.isActive(name, value);
            $input.disabled = !filter.inputIsEnabled(name, value);
        });
    };

    const updateSideClearButton = () => {
        if (!$sideClearButton) {
            return;
        }

        $sideClearButton.disabled = !filter.hasActive;
    };

    const updateOffCanvasClearButton = () => {
        if (!$offCanvasClearButton) {
            return;
        }

        $offCanvasClearButton.disabled = !filter.hasActive;
    };

    const updatePills = () => {
        $pillsContainer?.classList.toggle(
            $pillsContainer.dataset.hiddenClass,
            !filter.hasActive,
        );

        $pills.forEach(($pill) => {
            const { name, value } = $pill.dataset;

            $pill.classList.toggle('hidden', !filter.isActive(name, value));
        });
    };

    const updateCourses = () => {
        let hasMatches = false;

        $courses.forEach(($course) => {
            const courseMatches = filter.courseMatches($course.dataset.id);

            $course.classList.toggle('hidden', !courseMatches);

            hasMatches = hasMatches || courseMatches;
        });

        $stickyCoursesContainer?.classList.toggle('hidden', filter.hasActive);
        $coursesContainer.classList.toggle('hidden', !hasMatches);
        $noMatchesMessage.classList.toggle('hidden', hasMatches);
    };

    const updateOrderInputs = () => {
        $orderInputs.forEach(($input) => {
            $input.checked = $input.value === order;
        });
    };

    const updateCourseOrder = () => {
        if (appliedOrder === order) {
            return;
        }

        [$stickyCoursesList, $coursesContainer].forEach(($container) => {
            if (!$container) {
                return;
            }

            // Positions for every order are resolved server-side, so this only
            // has to compare integers rather than re-derive the collation.
            Array.from($container.children)
                .sort(
                    ($a, $b) =>
                        courseIdToOrderToIndex[$a.dataset.id][order] -
                        courseIdToOrderToIndex[$b.dataset.id][order],
                )
                .forEach(($course) => $container.appendChild($course));
        });

        appliedOrder = order;
    };

    const updateUi = () => {
        updateInputCounts();
        updateInputs();
        updateOrderInputs();
        updateSideClearButton();
        updateOffCanvasClearButton();
        updatePills();
        updateCourses();
        updateCourseOrder();
    };

    const updateOrderFromForm = ($form) => {
        if (!$form) {
            return;
        }

        const value = new FormData($form).get(ORDER_NAME);

        if (typeof value === 'string' && value !== '') {
            order = value;
        }
    };

    const scrollToTop = () => {
        const bodyTop = document.body.getBoundingClientRect().top;
        const coursesTop = $coursesContainer.getBoundingClientRect().top;
        const stickyContainerTop = parseInt(
            getComputedStyle($stickyContainer).getPropertyValue('top'),
            10,
        );
        const stickyContainerHeight =
            $stickyContainer.getBoundingClientRect().height;
        const stickyContainerBottom =
            stickyContainerTop + stickyContainerHeight;
        const gap = parseInt(
            getComputedStyle($coursesContainer).getPropertyValue('row-gap'),
            10,
        );

        const top = coursesTop - bodyTop - stickyContainerBottom - gap;

        window.scrollTo({
            top,
            behavior: 'smooth',
        });
    };

    const toggleOffCanvasForm = (isOpen) => {
        if (offCanvasFormIsOpen === isOpen) {
            return;
        }

        $offCanvasFormContainer.classList.toggle(
            'pointer-events-none',
            !isOpen,
        );
        $offCanvasFormContainer.classList.toggle('bg-black/50', isOpen);

        $offCanvasForm.classList.toggle('translate-x-full', !isOpen);

        offCanvasFormIsOpen = isOpen;

        toggleBodyLock(isOpen);

        if (isOpen) {
            offCanvasFormChangedWhileOpen = false;
        } else if (offCanvasFormChangedWhileOpen) {
            filter.updateFiltersFromForm($offCanvasForm);
            updateOrderFromForm($offCanvasForm);

            updateUi();
            scrollToTop();
        }
    };

    const updateSideFormContainerScroll = () => {
        const { top: elTop, bottom: elBottom } = $el.getBoundingClientRect();

        if (elTop === oldElTop) {
            return;
        }

        const { top, bottom } = $sideFormContainer.getBoundingClientRect();
        const { offsetHeight, scrollTop, scrollHeight } = $sideFormContainer;

        const topDiff = Math.max(0, top - elTop);

        if (topDiff < scrollTop) {
            $sideFormContainer.scrollTop = topDiff;
        }

        const overflowHeight = scrollHeight - offsetHeight - scrollTop;

        const bottomDiff = Math.max(0, bottom + overflowHeight - elBottom);

        if (bottomDiff > 0) {
            $sideFormContainer.scrollTop = scrollTop + bottomDiff;
        }

        oldElTop = elTop;
    };

    // Tracking

    const countMatchingCourses = () => {
        let count = 0;

        $courses.forEach(($course) => {
            if (filter.courseMatches($course.dataset.id)) {
                count += 1;
            }
        });

        return count;
    };

    /**
     * One event for every way the active filter set changes, so the whole
     * funnel is countable from a single name. `filter_action` says how it
     * changed and `result_count` says what it produced — a run of changes
     * ending in 0 results is the interesting case.
     *
     * Restoring state from the URL on load is deliberately not tracked: it is
     * page state rather than an interaction, and the pageview covers it.
     */
    const trackFilterChange = (action, name = null, value = null) => {
        pushToDataLayer({
            event: 'course_filter_change',
            filter_action: action,
            filter_name: name,
            filter_value: value,
            filter_count: filter.activeCount,
            result_count: countMatchingCourses(),
        });
    };

    const isOrderEvent = (event) =>
        event?.target instanceof HTMLInputElement &&
        event.target.name === ORDER_NAME;

    /**
     * Ordering rides in the same forms as the filters but changes neither the
     * active filter set nor the result count, so it gets its own event rather
     * than masquerading as a `course_filter_change` select.
     */
    const trackSortChange = () => {
        pushToDataLayer({
            event: 'course_filter_sort',
            sort_order: order,
            filter_count: filter.activeCount,
        });
    };

    // A `change` on either form comes from exactly one checkbox; report which
    // one and whether it went on or off.
    const trackInputChange = (event) => {
        const $input = event?.target;

        if (!($input instanceof HTMLInputElement)) {
            trackFilterChange('change');

            return;
        }

        let { name } = $input;

        if (name.endsWith('[]')) {
            name = name.slice(0, -2);
        }

        trackFilterChange(
            $input.checked ? 'select' : 'deselect',
            name,
            $input.value,
        );
    };

    // Event Handlers

    const handleOpenOffCanvasFormButtonClick = (event) => {
        event.preventDefault();
        event.stopPropagation();

        pushToDataLayer({
            event: 'course_filter_open',
            filter_count: filter.activeCount,
        });

        toggleOffCanvasForm(true);
    };

    const handleOffCanvasCloseButtonClick = (event) => {
        event.preventDefault();
        event.stopPropagation();

        // Off-canvas changes only reach the course list on close, so this is
        // where the selection is actually applied.
        pushToDataLayer({
            event: 'course_filter_apply',
            filter_count: filter.activeCount,
            result_count: countMatchingCourses(),
        });

        toggleOffCanvasForm(false);
    };

    const handlePillButtonClick = (event) => {
        const $pill = event.currentTarget.closest(dcn('-pill'));

        const { name, value } = $pill.dataset;

        filter.deactivateFilter(name, value);

        trackFilterChange('remove_pill', name, value);

        updateUi();
        scrollToTop();
    };

    const handleSideClearButtonClick = (event) => {
        event.preventDefault();
        event.stopPropagation();

        filter.clear();

        trackFilterChange('clear');

        updateUi();
    };

    const handleSideFormChange = (event) => {
        filter.updateFiltersFromForm($sideForm);
        updateOrderFromForm($sideForm);

        if (isOrderEvent(event)) {
            trackSortChange();
        } else {
            trackInputChange(event);
        }

        if ($currentPopover) {
            $currentPopover.classList.add('opacity-0');
            $currentPopover.classList.add('pointer-events-none');

            $currentPopover = null;
        }

        updateUi();
    };

    const handleOffCanvasFormChange = (event) => {
        offCanvasFormChangedWhileOpen = true;

        filter.updateFiltersFromForm($offCanvasForm);
        updateOrderFromForm($offCanvasForm);

        if (isOrderEvent(event)) {
            trackSortChange();
        } else {
            trackInputChange(event);
        }

        // Everything else is deferred to close, where the reordered list is
        // actually visible.
        updateInputs();
        updateOffCanvasClearButton();
    };

    const handleOffCanvasClearButtonClick = (event) => {
        event.preventDefault();
        event.stopPropagation();

        filter.clear();
        offCanvasFormChangedWhileOpen = false;

        trackFilterChange('clear');

        toggleOffCanvasForm(false);
        updateUi();
        scrollToTop();
    };

    const handleSubmit = (event) => {
        event.preventDefault();
    };

    const handleInputButtonClick = (event) => {
        event.preventDefault();
        event.stopPropagation();

        const $button = event.currentTarget;

        const $popover = $button.parentElement.querySelector(
            dcn('-inputPopover'),
        );

        if ($popover === $currentPopover) {
            return;
        }

        if ($currentPopover) {
            $currentPopover.classList.add('opacity-0');
            $currentPopover.classList.add('pointer-events-none');
        }

        $popover.classList.remove('opacity-0');
        $popover.classList.remove('pointer-events-none');

        $currentPopover = $popover;
    };

    const handleBodyClick = (event) => {
        // Popover

        if ($currentPopover && !$currentPopover.contains(event.target)) {
            $currentPopover.classList.add('opacity-0');
            $currentPopover.classList.add('pointer-events-none');

            $currentPopover = null;
        }

        // Off Canvas Form

        if (offCanvasFormIsOpen && !$offCanvasForm.contains(event.target)) {
            toggleOffCanvasForm(false);
        }
    };

    const handleAnimationFrame = () => {
        updateSideFormContainerScroll();

        requestAnimationFrame(handleAnimationFrame);
    };

    // Setup Event Handlers

    $openOffCanvasFormButton?.addEventListener(
        'click',
        handleOpenOffCanvasFormButtonClick,
    );

    $offCanvasCloseButton?.addEventListener(
        'click',
        handleOffCanvasCloseButtonClick,
    );

    $offCanvasClearButton?.addEventListener(
        'click',
        handleOffCanvasClearButtonClick,
    );

    $sideClearButton?.addEventListener('click', handleSideClearButtonClick);
    $sideForm?.addEventListener('change', handleSideFormChange);
    $sideForm?.addEventListener('submit', handleSubmit);

    $offCanvasForm?.addEventListener('change', handleOffCanvasFormChange);
    $offCanvasForm?.addEventListener('submit', handleSubmit);

    $inputButtons.forEach(($button) => {
        $button.addEventListener('click', handleInputButtonClick);
    });

    $pillButtons.forEach(($pillButton) => {
        $pillButton.addEventListener('click', handlePillButtonClick);
    });

    document.body.addEventListener('click', handleBodyClick);

    if ($sideFormContainer) {
        requestAnimationFrame(handleAnimationFrame);
    }

    // Restore State on Load

    const restoreState = () => {
        filter.updateFiltersFromForm($offCanvasForm);
        updateUi();
    };

    if (document.readyState === 'complete') {
        restoreState();
    } else {
        window.addEventListener('load', () => {
            setTimeout(restoreState);
        });
    }
});
